/**
 * TaggingRuleFormModal Component
 *
 * Modal form for creating tagging rules from transactions.
 * Pre-fills pattern from transaction description and allows tag selection.
 *
 * Features:
 * - Pre-filled pattern from transaction description (escaped for regex)
 * - Tag selector for choosing which tag to apply
 * - Pattern validation with real-time regex check
 * - Create Rule and Create & Apply buttons
 * - Name, pattern, tag, and priority fields
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import clsx from 'clsx';
import { X, Save, Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import type { Tag } from '@/types/accountingTags';
import * as accountingTagsApi from '@/services/accountingTagsApi';
import { getTagString } from '@/types/accountingTags';

/**
 * Source transaction info for pre-filling form fields
 */
export interface SourceTransaction {
  id: number;
  description: string;
}

export interface TaggingRuleFormModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback to close the modal */
  onClose: () => void;
  /** Source transaction for pre-filling pattern */
  sourceTransaction: SourceTransaction;
  /** Available tags for selection */
  availableTags: Tag[];
  /** Initial tag ID to pre-select (optional) */
  initialTagId?: number;
  /** Callback when rule is successfully created */
  onSuccess?: (ruleId: number) => void;
}

interface FormData {
  name: string;
  pattern: string;
  tagId: number | null;
  priority: number;
}

/**
 * Validate if a string is a valid regex pattern
 */
function isValidRegex(pattern: string): boolean {
  try {
    new RegExp(pattern);
    return true;
  } catch {
    return false;
  }
}

/**
 * Escape special regex characters in a string for use in a regex pattern
 */
function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * TaggingRuleFormModal for creating tagging rules from transactions.
 *
 * @example
 * <TaggingRuleFormModal
 *   isOpen={showModal}
 *   onClose={() => setShowModal(false)}
 *   sourceTransaction={{ id: 1, description: "AMAZON PURCHASE" }}
 *   availableTags={tags}
 *   initialTagId={5}
 *   onSuccess={(ruleId) => console.log('Created rule', ruleId)}
 * />
 */
export function TaggingRuleFormModal({
  isOpen,
  onClose,
  sourceTransaction,
  availableTags,
  initialTagId,
  onSuccess,
}: TaggingRuleFormModalProps) {
  // Form state
  const [formData, setFormData] = useState<FormData>({
    name: '',
    pattern: '',
    tagId: null,
    priority: 5,
  });

  // UI state
  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        name: '',
        pattern: escapeRegex(sourceTransaction.description),
        tagId: initialTagId ?? null,
        priority: 5,
      });
      setFormError(null);
    }
  }, [isOpen, sourceTransaction.description, initialTagId]);

  // Validate pattern
  const isPatternValid = useMemo(() => {
    return formData.pattern.length === 0 || isValidRegex(formData.pattern);
  }, [formData.pattern]);

  // Form validation
  const canSubmit = useMemo(() => {
    return (
      formData.name.trim().length > 0 &&
      formData.pattern.trim().length > 0 &&
      isPatternValid &&
      formData.tagId !== null &&
      !isSaving
    );
  }, [formData, isPatternValid, isSaving]);

  // Get selected tag object
  const selectedTag = useMemo(() => {
    return availableTags.find((tag) => tag.id === formData.tagId) ?? null;
  }, [availableTags, formData.tagId]);

  // Handle field changes
  const handleChange = useCallback(
    (field: keyof FormData, value: string | number | null) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      setFormError(null);
    },
    []
  );

  // Handle form submit
  const handleSubmit = useCallback(
    async (e: React.FormEvent, applyToTransaction: boolean = false) => {
      e.preventDefault();

      if (!canSubmit || formData.tagId === null) return;

      setIsSaving(true);
      setFormError(null);

      try {
        // Create the tagging rule
        const taggingRule = await accountingTagsApi.createTaggingRule(
          formData.name.trim(),
          formData.pattern.trim(),
          formData.tagId,
          formData.priority
        );

        // If should apply to transaction, add the tag
        if (applyToTransaction && selectedTag) {
          const tagString = getTagString(selectedTag);
          await accountingTagsApi.addTagToTransaction(
            sourceTransaction.id,
            tagString
          );
          toast.success('Tagging rule created and applied to transaction');
        } else {
          toast.success('Tagging rule created');
        }

        // Call success callback
        onSuccess?.(taggingRule.id);

        // Close the modal
        onClose();
      } catch (error) {
        setFormError(
          error instanceof Error ? error.message : 'Failed to create tagging rule'
        );
        toast.error(
          error instanceof Error ? error.message : 'Failed to create tagging rule'
        );
      } finally {
        setIsSaving(false);
      }
    },
    [canSubmit, formData, selectedTag, sourceTransaction.id, onSuccess, onClose]
  );

  // Handle Create & Apply button click
  const handleCreateAndApply = useCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault();
      // Create a synthetic form event
      const syntheticEvent = { preventDefault: () => {} } as React.FormEvent;
      await handleSubmit(syntheticEvent, true);
    },
    [handleSubmit]
  );

  // Handle modal close
  const handleClose = useCallback(() => {
    if (!isSaving) {
      onClose();
    }
  }, [isSaving, onClose]);

  // Handle backdrop click
  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        handleClose();
      }
    },
    [handleClose]
  );

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={handleBackdropClick}
      data-testid="tagging-rule-form-modal-backdrop"
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="tagging-rule-form-modal"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            Create Tagging Rule
          </h2>
          <button
            type="button"
            onClick={handleClose}
            disabled={isSaving}
            className={clsx(
              'p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100',
              'focus:outline-none focus:ring-2 focus:ring-blue-500',
              isSaving && 'cursor-not-allowed opacity-50'
            )}
            aria-label="Close modal"
          >
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="px-6 py-4 space-y-4">
            {/* Source Transaction Banner */}
            <div
              className="p-3 bg-blue-50 border border-blue-200 rounded-md"
              data-testid="source-transaction-banner"
            >
              <p className="text-sm font-medium text-blue-800 mb-1">
                Creating rule from transaction
              </p>
              <p
                className="text-sm text-blue-700 truncate"
                title={sourceTransaction.description}
              >
                {sourceTransaction.description}
              </p>
            </div>

            {/* Form Error */}
            {formError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md flex items-start gap-2">
                <AlertCircle
                  size={16}
                  className="text-red-600 flex-shrink-0 mt-0.5"
                />
                <p className="text-sm text-red-700">{formError}</p>
              </div>
            )}

            {/* Name */}
            <div>
              <label
                htmlFor="rule-name"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Rule Name <span className="text-red-500">*</span>
              </label>
              <input
                id="rule-name"
                type="text"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="e.g., Amazon Purchases"
                disabled={isSaving}
                className={clsx(
                  'w-full px-3 py-2 text-sm border rounded-md',
                  'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                  'disabled:bg-gray-50 disabled:cursor-not-allowed',
                  'border-gray-300'
                )}
                data-testid="rule-name-input"
              />
            </div>

            {/* Pattern */}
            <div>
              <label
                htmlFor="rule-pattern"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Pattern (Regex) <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  id="rule-pattern"
                  type="text"
                  value={formData.pattern}
                  onChange={(e) => handleChange('pattern', e.target.value)}
                  placeholder="e.g., amazon|amzn"
                  disabled={isSaving}
                  className={clsx(
                    'w-full px-3 py-2 text-sm font-mono border rounded-md',
                    'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                    'disabled:bg-gray-50 disabled:cursor-not-allowed',
                    !isPatternValid
                      ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                      : 'border-gray-300'
                  )}
                  data-testid="rule-pattern-input"
                />
                {!isPatternValid && (
                  <p className="mt-1 text-sm text-red-600">
                    Invalid regular expression
                  </p>
                )}
                {isPatternValid && formData.pattern.length > 0 && (
                  <p className="mt-1 text-xs text-gray-500">
                    Pattern pre-filled from transaction description
                  </p>
                )}
              </div>
            </div>

            {/* Tag Selector */}
            <div>
              <label
                htmlFor="tag-select"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Tag <span className="text-red-500">*</span>
              </label>
              <select
                id="tag-select"
                value={formData.tagId ?? ''}
                onChange={(e) =>
                  handleChange(
                    'tagId',
                    e.target.value ? parseInt(e.target.value, 10) : null
                  )
                }
                disabled={isSaving}
                className={clsx(
                  'w-full px-3 py-2 text-sm border rounded-md',
                  'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                  'disabled:bg-gray-50 disabled:cursor-not-allowed',
                  'border-gray-300'
                )}
                data-testid="tag-select"
              >
                <option value="">Select a tag...</option>
                {availableTags
                  .filter((tag) => tag.active)
                  .map((tag) => (
                    <option key={tag.id} value={tag.id}>
                      {tag.tagType}:{tag.value}
                      {tag.displayName && ` (${tag.displayName})`}
                    </option>
                  ))}
              </select>
            </div>

            {/* Priority */}
            <div>
              <label
                htmlFor="rule-priority"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Priority
              </label>
              <input
                id="rule-priority"
                type="number"
                min="1"
                max="100"
                value={formData.priority}
                onChange={(e) =>
                  handleChange('priority', parseInt(e.target.value, 10) || 5)
                }
                disabled={isSaving}
                className={clsx(
                  'w-24 px-3 py-2 text-sm border rounded-md',
                  'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                  'disabled:bg-gray-50 disabled:cursor-not-allowed',
                  'border-gray-300'
                )}
                data-testid="rule-priority-input"
              />
              <p className="mt-1 text-xs text-gray-500">
                Higher priority rules are checked first (1-100)
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={handleClose}
              disabled={isSaving}
              className={clsx(
                'px-4 py-2 text-sm font-medium rounded-md',
                'text-gray-700 hover:bg-gray-200',
                'focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2',
                'disabled:opacity-50 disabled:cursor-not-allowed'
              )}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleCreateAndApply}
              disabled={!canSubmit}
              className={clsx(
                'inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-md',
                'bg-green-600 text-white hover:bg-green-700',
                'focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2',
                'disabled:opacity-50 disabled:cursor-not-allowed'
              )}
              data-testid="create-and-apply-button"
            >
              {isSaving ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <CheckCircle size={16} />
              )}
              {isSaving ? 'Saving...' : 'Create & Apply to Transaction'}
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className={clsx(
                'inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-md',
                'bg-blue-600 text-white hover:bg-blue-700',
                'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
                'disabled:opacity-50 disabled:cursor-not-allowed'
              )}
              data-testid="create-rule-button"
            >
              {isSaving ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Save size={16} />
              )}
              {isSaving ? 'Saving...' : 'Create Rule'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default TaggingRuleFormModal;
